import './Billing.css';
import { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Box,
} from '@material-ui/core';
import { TableContainer } from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { config } from './config';
import { ERRORS } from '../../../utils/constants';
import { handleError } from '../../../utils/config';
import { useHistory } from 'react-router-dom';
import { RoutesFun } from '../../../schemas';
import { useFetchData } from '../../../hooks/useFetchData';
import AddPlanModal from '../../../components/Modals/SuperAdmin/AddPlan/AddPlanModal';
import ActiveFeaturesModal from '../../../components/Modals/SuperAdmin/ActiveFeatures/ActiveFeaturesModal';
import TitleHeader from '../../../components/MasterComponent/TitleHeader/TitleHeader';

// Helper function to calculate end date
const calculateEndDate = (startDate: string, planDays: number): string => {
  const start = new Date(startDate);
  const end = new Date(start);
  end.setDate(end.getDate() + planDays);
  return end.toISOString().split('T')[0];
};

// Helper function to calculate billing date (same as end date for monthly billing)
const calculateBillingDate = (startDate: string, planDays: number): string => {
  return calculateEndDate(startDate, planDays);
};

// Helper function to check if subscription is currently active
const isSubscriptionActive = (startDate: string, planDays: number): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + planDays);
  end.setHours(23, 59, 59, 999);

  return today >= start && today <= end;
};

// Helper function to format date for display
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper function to get subscription mode sort order
const getSubscriptionModeOrder = (mode: string): number => {
  const order: { [key: string]: number } = {
    Monthly: 1,
    Quarterly: 2,
    Yearly: 3,
    'One Time': 4,
  };
  return order[mode] || 999;
};

const AdminPanelBilling = () => {
  const [requestsData, setRequestsData] = useState([]);
  const [networkError, setNetworkError] = useState('');
  const [activeSubData, setActiveSubData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [activeSubLoader, setActiveSubLoader] = useState(true);
  const [showActiveFeaturesModal, setShowActiveFeaturesModal] = useState(false);
  const [activePlanData, setActivePlanData] = useState({
    features: [],
    planName: '',
    planId: null,
    companyName: '',
  });
  const [allFeatures, setAllFeatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [selectedRequestRow, setSelectedRequestRow] = useState<any>(null);
  const [quotations, setQuotations] = useState<{ [key: number]: any }>({});

  const { fetch, loader: isSubmitting } = useFetchData({
    fetchData: (params) => config.addPlanFeatures(params),
    fetchOnMount: false,
  });

  const { fetch: createPlanHandler, loader: isPlanReqSubmitted } = useFetchData(
    {
      fetchData: (params) => config.setBillingRequest(params),
      fetchOnMount: false,
    }
  );

  const history = useHistory();
  const Routes = RoutesFun();
  const closeActiveFeaturesModal = () => setShowActiveFeaturesModal(false);
  const closeAddPlanModal = () => {
    setShowAddPlanModal(false);
    setSelectedRequestRow(null);
  };

  const handleAddPlanSubmit = async (payload: any) => {
    // Store quotation data without API call

    try {
      await createPlanHandler(payload);
      // Refresh active subscriptions after successful update
      closeAddPlanModal();
      await getAllRequest();
    } catch (error) {
      console.error('Error updating features:', error);
      throw error;
    }

    if (selectedRequestRow) {
      setQuotations((prev) => ({
        ...prev,
        [selectedRequestRow.id]: payload,
      }));
      closeAddPlanModal();
    }
  };

  // const handleSubmitQuotation = async (row: any) => {
  //   const quotation = quotations[row.id];
  //   if (!quotation) return;

  //   const apiPayload = {
  //     CompanyId: row.id,
  //     PlanName: quotation.planName,
  //     PlanPrice: parseFloat(quotation.planAmount),
  //     PlanFeatures: quotation.features.map((f: any) => ({
  //       feature_id: f.id,
  //       ...(f.max_limit !== undefined && { max_limit: f.max_limit }),
  //     })),
  //   };

  //   try {
  //     // Call your API here
  //     // await config.submitPlanQuotation(apiPayload);
  //     console.log('Submitting quotation:', apiPayload);

  //     // Refresh requests after successful submission
  //     // await getAllRequest();

  //     // Remove quotation from state after successful submission
  //     setQuotations((prev) => {
  //       const newQuotations = { ...prev };
  //       delete newQuotations[row.id];
  //       return newQuotations;
  //     });
  //   } catch (error) {
  //     console.error('Error submitting quotation:', error);
  //     throw error;
  //   }
  // };

  const handleFeatureSubmit = async (payload: any) => {
    try {
      await fetch(payload);
      // Refresh active subscriptions after successful update
      closeActiveFeaturesModal();
      await getActiveSub();
    } catch (error) {
      console.error('Error updating features:', error);
      throw error;
    }
  };

  // Filter, calculate dates, sort, and search for active subscriptions
  const filteredActiveSubData = useMemo(() => {
    return activeSubData
      .map((row: any) => ({
        ...row,
        startDate: row.PlanDate,
        endDate: calculateEndDate(row.PlanDate, row.PlanDays),
        billingDate: calculateBillingDate(row.PlanDate, row.PlanDays),
        isCurrentlyActive: isSubscriptionActive(row.PlanDate, row.PlanDays),
      }))
      .filter((row: any) => row.isCurrentlyActive)
      .sort((a: any, b: any) => {
        // Sort by plan price (highest first)
        const priceA = parseFloat(a.planPrice) || 0;
        const priceB = parseFloat(b.planPrice) || 0;

        if (priceA !== priceB) {
          return priceB - priceA; // Descending order (highest first)
        }

        // If same price, sort by subscription mode (Monthly first, then others)
        const orderA = getSubscriptionModeOrder(a.SubscriptionMode);
        const orderB = getSubscriptionModeOrder(b.SubscriptionMode);

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // If same subscription mode, sort by organization name
        return a.name.localeCompare(b.name);
      })
      .filter((row: any) => {
        // Apply search filter
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        const nameMatch = row.name.toLowerCase().includes(searchLower);
        const planTypeMatch = row.PlanType.toLowerCase().includes(searchLower);

        return nameMatch || planTypeMatch;
      });
  }, [activeSubData, searchTerm]);

  //api calls
  const getAllFeatures = async () => {
    try {
      const res = await config.getAllFeatures();
      // const modifyFeature: any = {};
      if (res.length) {
        setAllFeatures(res);
        // res.forEach((el: any) => (modifyFeature[el.name] = el.id));
        // setFeatures(modifyFeature);
      }
    } catch (e: any) {
      setNetworkError(e === ERRORS.NETWORK_ERROR ? e : '');
      if (e && e.error && e.error.message) {
        handleError(e, history);
      }
    }
  };

  const getAllRequest = async () => {
    try {
      setLoader(true);
      const res = await config.getAllBillingRequests();
      setRequestsData(res);
    } catch (e: any) {
      setNetworkError(e === ERRORS.NETWORK_ERROR ? e : '');
      if (e && e.error && e.error.message) {
        handleError(e, history);
      }
    } finally {
      setLoader(false);
    }
  };

  const getActiveSub = async () => {
    try {
      setActiveSubLoader(true);
      const res = await config.getActiveSub();
      setActiveSubData(res);
    } catch (e: any) {
      setNetworkError(e === ERRORS.NETWORK_ERROR ? e : '');
      if (e && e.error && e.error.message) {
        handleError(e, history);
      }
    } finally {
      setActiveSubLoader(false);
    }
  };

  useEffect(() => {
    getActiveSub();
    getAllRequest();
    getAllFeatures();
  }, []);

  return (
    <>
      {/* <div className="OrgText">Plan Activation</div>{' '} */}
      <TitleHeader>Plan Activation</TitleHeader>
      <TableContainer
        className="accContainer mt-20"
        sx={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <Table
          className="table adminTable OrgTable"
          size="small"
          aria-label="a dense table"
          stickyHeader
        >
          <TableHead className="admintableHead">
            <TableRow>
              <TableCell className="adminTableheading" align="right" width="2%">
                S.No.
              </TableCell>
              <TableCell className="adminTableheading" width={'15%'}>
                Organization Name
              </TableCell>
              <TableCell className="adminTableheading" width={'12%'}>
                Plan Type
              </TableCell>
              <TableCell className="adminTableheading" width={'20%'}>
                Organization Email Address
              </TableCell>
              {/* <TableCell className="adminTableheading" width={'25%'}>
                Quotation Details
              </TableCell> */}
              <TableCell className="adminTableheading" width={'8%'}>
                Status
              </TableCell>
              <TableCell className="adminTableheading" width={'10%'}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loader ? (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Loading...</TableCell>
              </TableRow>
            ) : requestsData.length ? (
              requestsData?.map((row: any, index: number) => {
                return (
                  <TableRow key={row.id + index}>
                    <TableCell align="right">{index + 1}</TableCell>
                    <TableCell className="upperCase">
                      {row.CompanyName}
                    </TableCell>
                    <TableCell>{row.name || 'N/A'}</TableCell>
                    <TableCell>{row.CompanyEmail}</TableCell>

                    <TableCell>{row.status || 'Pending'}</TableCell>
                    <TableCell>
                      <button
                        className="btn btn-primary btn-sm"
                        // onClick={() => handleSubmitQuotation(row)}
                        onClick={() => {
                          setSelectedRequestRow(row);
                          setShowAddPlanModal(true);
                        }}
                      >
                        Add Quotation
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>No Requests</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        className="mt-40"
      >
        <div className="OrgText">Active Subscriptions</div>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by organization or plan type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '350px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <TableContainer
        className="accContainer mt-20"
        sx={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <Table
          className="table adminTable OrgTable"
          size="small"
          aria-label="a dense table"
          stickyHeader
        >
          <TableHead className="admintableHead">
            <TableRow>
              <TableCell className="adminTableheading" align="right" width="2%">
                S.No.
              </TableCell>
              <TableCell className="adminTableheading">
                Organization Name
              </TableCell>
              <TableCell className="adminTableheading">Plan Type</TableCell>
              <TableCell className="adminTableheading">Start Date</TableCell>
              <TableCell className="adminTableheading">End Date</TableCell>
              <TableCell className="adminTableheading">Billing Date</TableCell>
              <TableCell className="adminTableheading">
                Monthly plan amount($)
              </TableCell>
              <TableCell className="adminTableheading">Status</TableCell>
              <TableCell className="adminTableheading">
                Subscription Mode
              </TableCell>
              <TableCell className="adminTableheading">Services</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeSubLoader ? (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Loading...</TableCell>
              </TableRow>
            ) : filteredActiveSubData.length ? (
              filteredActiveSubData?.map((row: any, index: number) => (
                <TableRow key={row.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <span
                      className="upperCase clickble"
                      onClick={() =>
                        history.push({
                          pathname: Routes.AdminPanelOrganizationBillingDetails,
                          state: {
                            CompanyName: row.name,
                          },
                        })
                      }
                    >
                      {row.name}
                    </span>
                  </TableCell>
                  <TableCell>{row.PlanType}</TableCell>
                  <TableCell>{formatDate(row.startDate)}</TableCell>
                  <TableCell>{formatDate(row.endDate)}</TableCell>
                  <TableCell>{formatDate(row.billingDate)}</TableCell>
                  <TableCell>
                    ${row.planPrice ? row.planPrice : '0.00'}
                  </TableCell>
                  <TableCell>
                    {row.isActive == 1 ? 'Active' : 'Inactive'}
                  </TableCell>
                  <TableCell>{row.SubscriptionMode}</TableCell>
                  <TableCell>
                    {row.SubscriptionStatus === 'trialing' ? (
                      <span
                        style={{
                          color: '#757575',
                          fontStyle: 'italic',
                          fontSize: '13px',
                        }}
                      >
                        Available after trial period
                      </span>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setActivePlanData({
                            features: row?.features || [],
                            planName: row?.PlanType || '',
                            planId: row?.company_plan_id || null,
                            companyName: row?.name || '',
                          });
                          setShowActiveFeaturesModal(true);
                        }}
                      >
                        See all active features
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  {searchTerm
                    ? 'No subscriptions found matching your search'
                    : 'No Active Subscriptions'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showActiveFeaturesModal && (
        <ActiveFeaturesModal
          onClose={closeActiveFeaturesModal}
          show={showActiveFeaturesModal}
          features={activePlanData.features}
          planName={activePlanData.planName}
          planId={activePlanData.planId}
          companyName={activePlanData.companyName}
          allFeatures={allFeatures}
          handleFeatureSubmit={handleFeatureSubmit}
          isSubmitting={isSubmitting}
        />
      )}
      {showAddPlanModal && selectedRequestRow && (
        <AddPlanModal
          show={showAddPlanModal}
          onClose={closeAddPlanModal}
          allFeatures={allFeatures}
          companyName={selectedRequestRow.CompanyName}
          companyId={selectedRequestRow.CompanyId} // Add this
          planName={selectedRequestRow.name} // Add this
          requestData={selectedRequestRow}
          existingQuotation={quotations[selectedRequestRow.id]}
          handlePlanSubmit={handleAddPlanSubmit}
          isSubmitting={isPlanReqSubmitted}
        />
      )}
    </>
  );
};

export default AdminPanelBilling;
